/**
 * Data Import — pure mapping from a Headlight `/1.0/<Entity>/Schema` body to the target-schema shape
 * pict-section-dataimport's mapping step consumes:
 *   { Entities: [ { Entity, GUIDName, Fields: [ { Name, Type, Size, Required, IsGUID, ForeignKeyEntity } ] } ], Order }
 *
 * Headlight's schema endpoint returns JSON-schema (`{ properties: { Col: { type, size } } }`); meadow's
 * native shape (`{ Columns: [...] }`) is also accepted. System / audit / auto-PK columns are dropped so
 * the column-mapping UI only shows real, settable fields. No pict / REST dependency — unit-testable.
 *
 * This is the browser-clean equivalent of pict-section-dataimport's own Meadow schema adapter, which we
 * cannot use directly because it hard-requires `pict.EntityProvider` (the Headlight config app drives
 * everything through HeadlightRestClient instead). v1 is single-entity, so there is no foreign-key
 * inference and the Order is just `[ entity ]`.
 */

// Audit / lifecycle columns Meadow stamps on every entity — noise in a column-mapping UI.
const SYSTEM_COLUMNS =
{
	CreateDate: 1, CreatingIDUser: 1, UpdateDate: 1, UpdatingIDUser: 1,
	Deleted: 1, DeleteDate: 1, DeletingIDUser: 1,
};

/**
 * Pull a normalized column list out of a schema body (JSON-schema `properties`, meadow `Columns`, or a
 * bare `Schema` array).
 * @param {Record<string, any>} pSchemaBody
 * @returns {Array<Record<string, any>>}
 */
function extractColumns(pSchemaBody)
{
	const tmpBody = pSchemaBody || {};
	if (Array.isArray(tmpBody.Columns))
	{
		return tmpBody.Columns;
	}
	if (Array.isArray(tmpBody.Schema))
	{
		return tmpBody.Schema;
	}
	if (tmpBody.properties && (typeof tmpBody.properties === 'object'))
	{
		return Object.keys(tmpBody.properties).map((pKey) => Object.assign({ Column: pKey }, tmpBody.properties[pKey]));
	}
	return [];
}

/**
 * Map one entity's schema body to the dataimport target-schema descriptor.
 * @param {string} pEntityName
 * @param {Record<string, any>} pSchemaBody
 * @returns {{Entities:Array<any>, Order:Array<string>}}
 */
function mapEntitySchema(pEntityName, pSchemaBody)
{
	const tmpGUIDName = `GUID${pEntityName}`;
	const tmpColumns = extractColumns(pSchemaBody);
	const tmpFields = tmpColumns.map((pColumn) =>
	{
		const tmpName = pColumn.Column || pColumn.Name || pColumn.column;
		return {
			Name: tmpName,
			Type: pColumn.DataType || pColumn.Type || pColumn.type || 'string',
			Size: Number(pColumn.Size || pColumn.size || 0) || 0,
			Required: !!(pColumn.Required || pColumn.NonNull),
			IsGUID: (tmpName === tmpGUIDName),
			ForeignKeyEntity: '',
		};
	}).filter((pField) => !!pField.Name
		&& (pField.Name !== `ID${pEntityName}`)      // the auto-increment primary key
		&& !SYSTEM_COLUMNS[pField.Name]);            // audit / lifecycle columns

	return {
		Entities: [ { Entity: pEntityName, GUIDName: tmpGUIDName, Fields: tmpFields } ],
		Order: [ pEntityName ],
	};
}

module.exports =
{
	SYSTEM_COLUMNS,
	extractColumns,
	mapEntitySchema,
};
